import { Avatar, Flex, Text } from "@chakra-ui/react";
import { OptionProps, Select, GroupBase, Props } from "chakra-react-select";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import {
  BorrowerForTheCompanyModel,
  useBorrowersForCompany,
} from "hooks/useBorrowersForTheCompany";
import { AVATAR_URL } from "utils/constants";

const CustomOption = ({ data, innerProps }: OptionProps<BorrowerForTheCompanyModel>) => (
  <Flex
    {...innerProps}
    p={2}
    gap={4}
    borderRadius="md"
    _hover={{ bg: "gray.50" }}
    cursor="pointer"
    alignItems="center"
  >
    <Avatar size="sm" src={data.avatar ? `${AVATAR_URL}${data.id}` : ""} />
    <Flex direction="column" alignItems="start">
      <Text color="gray.600">{data.full_name}</Text>
      <Text fontSize="sm" color="gray.500">
        {data.company_name}
      </Text>
    </Flex>
  </Flex>
);

interface BorrowerSelectProps
  extends Props<BorrowerForTheCompanyModel, false, GroupBase<BorrowerForTheCompanyModel>> {
  borrower: BorrowerForTheCompanyModel | null;
  setBorrower: (borrower: BorrowerForTheCompanyModel | null) => void;
}

export const BorrowerSelect = ({ borrower, setBorrower, ...props }: BorrowerSelectProps) => {
  const { user } = useTenant();
  const { data: borrowers, isLoading } = useBorrowersForCompany(user.lender_company.id);

  return (
    <Select<BorrowerForTheCompanyModel>
      options={borrowers?.sort((a, b) => a.full_name.localeCompare(b.full_name)) ?? []}
      getOptionLabel={b => b.full_name}
      getOptionValue={b => b.id}
      components={{ Option: CustomOption }}
      isLoading={isLoading}
      chakraStyles={{
        placeholder: provided => ({
          ...provided,
          textAlign: "left",
        }),
        singleValue: provided => ({
          ...provided,
          textAlign: "left",
        }),
      }}
      placeholder="Select a borrower..."
      noOptionsMessage={() => "No borrowers found."}
      onChange={newValue => setBorrower(newValue)}
      value={borrower}
      variant="primary"
      filterOption={(option, inputValue) =>
        [option.data.full_name, option.data.company_name].some(field =>
          field?.toLowerCase().includes(inputValue.toLowerCase())
        )
      }
      isClearable={true}
      {...props}
    />
  );
};
